import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { AuthActions } from "src/redux/Auth/actions";
import { Auth } from "src/redux/Auth/types";
import { getTermsState } from "src/utils/auth";
import { ModalActions } from "src/redux/Modal/actions";
import { SpinnerActions } from "src/redux/Spinner/actions";
import ForgotPassword from "./ForgotPassword";
import RecommendMFA from "./RecommendMFA";
import TermsGate from "src/components/views/TermsGate";
import Button from "src/components/common/Button";
import styles from "./styles.module.scss";

const LoginForm = ({ onLogin }) => {
    const dispatch = useDispatch();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError("Please enter your email and password");
            return;
        }
        setError(null);
        dispatch(SpinnerActions.Open());
        try {
            await onLogin(email, password);
        } catch (err) {
            setError(err.message || "Login failed");
        } finally {
            dispatch(SpinnerActions.Close());
        }
    };

    return (
        <form className={styles.login} onSubmit={handleSubmit}>
            <h1>Login</h1>
            <div className={styles.splitter} />

            <div className={styles.body}>
                <label>
                    Work email address
                    <input
                        type="email"
                        autoFocus
                        placeholder="name@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="login-email-input"
                    />
                </label>

                <label>
                    Password
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="login-password-input"
                    />
                    <span
                        data-testid="forgot-password-link"
                        onClick={() =>
                            dispatch(
                                ModalActions.Open(ForgotPassword, {
                                    ignoreClickOutside: true,
                                    width: 440,
                                    customProps: { email },
                                })
                            )
                        }
                    >
                        <span className={styles.forgotPassword}>
                            Forgot your password?
                        </span>
                    </span>
                </label>
            </div>

            {error && (
                <div className={styles.error} data-testid="login-error">
                    {error}
                </div>
            )}

            <div className={styles.footer}>
                <Button
                    value="Login"
                    onClick={handleSubmit}
                    dataTestId="login-button"
                />
            </div>
        </form>
    );
};

const MfaCodePrompt = ({ sessionId, onVerify }) => {
    const dispatch = useDispatch();
    const [code, setCode] = useState("");
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!code || code.length !== 6) {
            setError("Please enter the 6-digit code");
            return;
        }
        setError(null);
        dispatch(SpinnerActions.Open());
        try {
            await onVerify(sessionId, code);
        } catch (err) {
            setError(err.message || "Verification failed");
        } finally {
            dispatch(SpinnerActions.Close());
        }
    };

    return (
        <form className={styles.login} onSubmit={handleSubmit}>
            <h1>Verification</h1>
            <div className={styles.splitter} />

            <div className={styles.body}>
                <div className={styles.mfaMessage}>
                    Open your authenticator app and enter the 6-digit code.
                </div>

                <label>
                    Verification Code
                    <input
                        type="text"
                        autoFocus
                        maxLength={6}
                        placeholder="000000"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                        required
                        data-testid="mfa-code-input"
                        autoComplete="one-time-code"
                    />
                </label>
            </div>

            {error && (
                <div className={styles.error} data-testid="mfa-error">
                    {error}
                </div>
            )}

            <div className={styles.footer}>
                <Button
                    value="Verify"
                    onClick={handleSubmit}
                    dataTestId="mfa-verify-button"
                />
            </div>
        </form>
    );
};

export default () => {
    const dispatch = useDispatch();
    const [step, setStep] = useState("login"); // "login" | "mfa"
    const [mfaState, setMfaState] = useState(null); // {mfa_type}
    const [credentials, setCredentials] = useState(null); // {email, password}

    const handleLogin = async (email, password) => {
        const result = await dispatch(AuthActions.DirectLogin(email, password));

        if (result.mfa_required) {
            setCredentials({ email, password });
            setMfaState(result);
            setStep("mfa");
        } else if (result.success && !result.mfa_configured) {
            // Login complete, show Security Notice as a modal (matches old flow).
            // Unless the terms gate is up: it stands in front of the modal, and
            // the settings fetches that run when the user accepts close any open
            // modal, so the notice would be discarded unseen. Hand it to the gate
            // to raise once it opens, and open the gate here.
            //
            // The gate is opened here rather than left to CompleteLogin, because a
            // session that is also mfa_pending never reaches CompleteLogin: the
            // login action returns early so the app does not boot. Deferring the
            // notice to a gate nobody opened left the screen unchanged, which is
            // what a user with both flags saw.
            if (getTermsState().pending) {
                const terms = getTermsState();
                dispatch({
                    type: Auth.MFA_NOTICE_DEFERRED,
                    mfaEnforced: result.mfa_enforced,
                    mfaPending: result.mfa_pending,
                });
                dispatch({
                    type: Auth.TERMS_REQUIRED,
                    version: terms.version,
                    effectiveDate: terms.effectiveDate,
                });
                dispatch(
                    ModalActions.Open(TermsGate, {
                        ignoreClickOutside: true,
                        onClose: () => dispatch(AuthActions.Logout()),
                    })
                );
            } else {
                dispatch(
                    ModalActions.Open(RecommendMFA, {
                        width: 650,
                        ignoreClickOutside: true,
                        customProps: { mfaEnforced: result.mfa_enforced },
                    })
                );
            }
        }
        // If result.success && mfa_configured, CompleteLogin already dispatched
    };

    const handleVerifyMfa = async (_sessionId, code) => {
        await dispatch(AuthActions.VerifyMfa(credentials.email, credentials.password, code));
        setCredentials(null); // Clear credentials after use
        // CompleteLogin already dispatched by VerifyMfa action
    };

    if (step === "mfa" && mfaState) {
        return (
            <MfaCodePrompt
                sessionId={mfaState.mfa_session}
                onVerify={handleVerifyMfa}
            />
        );
    }

    return <LoginForm onLogin={handleLogin} />;
};
